import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { logger } from "../services/logService";
import { teststepsService } from "../services/teststepsService";

import ErrorBoundary from "../components/ErrorBoundary";
import TestCaseFormModal from "../components/TestCaseFormModal";
import { projectService } from "../services/projectService";
import { testcaseService } from "../services/testcaseService";
import { jobService } from "../services/jobService";
import { setProjectsData } from "../redux/actions";
import sectionService from "../services/sectionService";
import SectionTree from "../components/testcase/SectionTree";
import SectionFormModal from "../components/testcase/SectionFormModal";
import DeleteSectionModal from "../components/testcase/DeleteSectionModal";
import SectionBasedTestCaseView from "../components/testcase/SectionBasedTestCaseView";
import AIGenerateTestCasesModal from "../components/AIGenerateTestCasesModal";
import TestCasesPreviewModal from "../components/TestCasesPreviewModal";
import TestCasesEmptyState from "../components/testcase/TestCasesEmptyState";
import TestCaseDetailPanel from "../components/testcase/TestCaseDetailPanel";
import TestCaseHistoryPage from "./TestCaseHistoryPage";
import PushNewDefectModal from "../components/defects/PushNewDefectModal";
import toast from "react-hot-toast";
import {
  Plus,
  X as Close,
  Upload,
  Download,
  Settings,
  AlertCircle,
  Play,
} from "lucide-react";
import { useCan } from "../hooks/useCan";

import { useDispatch } from "react-redux";
import { getStoredClientId } from "../utils/tokenUtils";
import { setLastTestCaseForHistory } from "../utils/testCaseHistoryNav";
import PageSkeleton from "../components/skeletons/PageSkeleton";
import TableSkeleton from "../components/skeletons/TableSkeleton";

const TestCaseManagementPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get("project_id");
  const projectName = searchParams.get("project_name") || "";
  const branchId = searchParams.get("branch_id"); // Get branch_id from URL
  const suiteId = searchParams.get("suite_id"); // Get suite_id from URL (for MULTIPLE_TEST_SUITES)
  const baselineId = searchParams.get("baseline_id"); // Get baseline_id from URL (for viewing baseline test cases)
  const viewOnly = searchParams.get("view_only") === "true"; // View-only mode when viewing baseline test cases
  const clientId = searchParams.get("client_id") || getStoredClientId();
  const [showPushDefectModal, setShowPushDefectModal] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [sectionStructure, setSectionStructure] = useState([]);
  const [rootSectionId, setRootSectionId] = useState(null);
  const [rootSectionName, setRootSectionName] = useState("Root");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const dispatch = useDispatch();

  console.log({ currentSection });
  // Permission checks - must be called before any conditional returns

  const { can: canViewTestCases } = useCan("test_suits_cases_sections", "view");
  const { can: canCreateTestCase } = useCan(
    "test_suits_cases_sections",
    "add_edit",
  );
  const { can: canEditTestCase } = useCan(
    "test_suits_cases_sections",
    "add_edit",
  );
  const { can: canDeleteTestCase } = useCan(
    "test_suits_cases_sections",
    "permanently_delete",
  );
  const { can: canGenerateAI } = useCan("generate_test_case_ai", "add_edit");
  const { can: canCreateSection } = useCan(
    "test_suits_cases_sections",
    "add_edit",
  );

  // In view-only mode (when viewing baseline test cases), disable all edit actions
  const isViewOnlyMode = viewOnly && baselineId;
  const effectiveCanCreateTestCase = canCreateTestCase && !isViewOnlyMode;
  const effectiveCanEditTestCase = canEditTestCase && !isViewOnlyMode;
  const effectiveCanDeleteTestCase = canDeleteTestCase && !isViewOnlyMode;
  const effectiveCanCreateSection = canCreateSection && !isViewOnlyMode;

  // Grouped test cases state
  const [groupedTestCases, setGroupedTestCases] = useState(null);
  const [groupedTestCasesLoading, setGroupedTestCasesLoading] = useState(false);
  const [allTestCasesFlat, setAllTestCasesFlat] = useState([]); // flat list from null-section fetch
  const [fullSectionStructure, setFullSectionStructure] = useState(null); // Store full section tree for lookup
  // Modal states
  const [showTestCaseModal, setShowTestCaseModal] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);

  // Section modal states
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionLoading, setSectionLoading] = useState(false);

  // Delete section modal states
  const [showDeleteSectionModal, setShowDeleteSectionModal] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);

  // Test steps states
  const [showStepsModal, setShowStepsModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [stepsLoading, _setStepsLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [testSteps, _setTestSteps] = useState([]);

  // Filter states
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    tags: [],
    created_by: "",
    assignedTo: "", // 'all' | 'unassigned' | userId
  });
  const [tempFilters, setTempFilters] = useState({
    status: "",
    tags: [],
    created_by: "",
    assignedTo: "",
  });
  const [projectUsers, setProjectUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);

  // Project structure state
  const [projectStructure, setProjectStructure] = useState(null);
  const [loadingProjectStructure, setLoadingProjectStructure] = useState(false);
  const [projectSettings, setProjectSettings] = useState({
    ai_test_case_generation: false,
    test_case_approval: false,
  });

  // Track if there are any test cases
  const [hasTestCases, setHasTestCases] = useState(false);
  const [checkingTestCases, setCheckingTestCases] = useState(true);

  // AI Generation states
  const [showAIModal, setShowAIModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [creatingAITestCases, setCreatingAITestCases] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTestCases, setPreviewTestCases] = useState(null);
  const [previewMetadata, setPreviewMetadata] = useState(null);
  const [exportSelectionMode, setExportSelectionMode] = useState(false);
  const [selectedTestCaseIdsForExport, setSelectedTestCaseIdsForExport] =
    useState([]);
  const [exportStartLoading, setExportStartLoading] = useState(false);

  // Track pending action when user needs to create section first
  const [pendingAction, setPendingAction] = useState(null); // 'create_test_case' | 'generate_ai' | null

  // Detail panel state
  const [selectedTestCaseId, setSelectedTestCaseId] = useState(null);
  const [detailPanelViewMode, setDetailPanelViewMode] = useState(true);
  const [showDetailPanel, setShowDetailPanel] = useState(false);

  // History panel state
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [historyTestCaseId, setHistoryTestCaseId] = useState(null);

  // Sidebar mode: 'subgroups' (default) | 'all'
  const [sidebarMode, setSidebarMode] = useState("all");
  // Section to scroll to in 'all' mode
  const [scrollToSectionId, setScrollToSectionId] = useState(null);

  // Active test case id — drives compact table mode
  const [activeTestCaseId, setActiveTestCaseId] = useState(null);

  // Track if we've already auto-selected the first section to prevent multiple selections
  const hasAutoSelectedRef = useRef(false);

  const hasAppliedFilters = useMemo(() => {
    const hasStatus =
      typeof filters.status === "string" && filters.status.trim() !== "";
    const hasTags =
      Array.isArray(filters.tags) &&
      filters.tags.some((tag) => typeof tag === "string" && tag.trim() !== "");
    const hasCreatedBy =
      typeof filters.created_by === "string" &&
      filters.created_by.trim() !== "";
    const hasAssignedTo =
      typeof filters.assignedTo === "string" &&
      filters.assignedTo.trim() !== "" &&
      filters.assignedTo !== "all";
    return hasStatus || hasTags || hasCreatedBy || hasAssignedTo;
  }, [filters]);

  const buildExportFilters = useCallback(() => {
    const exportFilters = {};
    if (filters.status) exportFilters.status = filters.status;
    if (Array.isArray(filters.tags) && filters.tags.length > 0) {
      exportFilters.tags = filters.tags.filter(
        (tag) => typeof tag === "string" && tag.trim() !== "",
      );
    }
    if (filters.created_by) exportFilters.created_by = filters.created_by;
    if (filters.assignedTo && filters.assignedTo !== "all") {
      exportFilters.assignedTo = filters.assignedTo;
    }
    // Preserve project context filters used in listing flow.
    if (branchId) exportFilters.branch_id = branchId;
    if (suiteId) exportFilters.suite_id = suiteId;
    return exportFilters;
  }, [filters, branchId, suiteId]);

  const handleExportAction = useCallback(async () => {
    if (!projectId) return;

    if (!exportSelectionMode) {
      setExportSelectionMode(true);
      setSelectedTestCaseIdsForExport([]);
      return;
    }

    const selectedIds = Array.isArray(selectedTestCaseIdsForExport)
      ? selectedTestCaseIdsForExport.filter(Boolean)
      : [];

    let selectionMode = "ALL";
    if (selectedIds.length > 0) {
      selectionMode = "SELECTED";
    } else if (hasAppliedFilters) {
      selectionMode = "FILTERED";
    }

    const payload = {
      project_id: projectId,
      includeDeleted: false,
      includeRejected: false,
      selectionMode,
    };

    if (selectionMode === "SELECTED") {
      payload.selectedTestCaseIds = selectedIds;
    } else if (selectionMode === "FILTERED") {
      payload.filters = buildExportFilters();
    }

    setExportStartLoading(true);
    try {
      const res = await jobService.startExport(payload);
      const jobId = (res?.data && res.data.job_id) || res?.job_id;
      if (jobId) {
        toast.success("Export job queued");
        setExportSelectionMode(false);
        setSelectedTestCaseIdsForExport([]);
      } else {
        toast.error("Failed to queue export job");
      }
    } catch (error) {
      logger.error(error, "Failed to start export job");
      toast.error(error?.response?.data?.message || "Failed to start export");
    } finally {
      setExportStartLoading(false);
    }
  }, [
    projectId,
    exportSelectionMode,
    selectedTestCaseIdsForExport,
    hasAppliedFilters,
    buildExportFilters,
  ]);

  // Load project structure
  const loadProjectStructure = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoadingProjectStructure(true);
      // Get client_id from URL params first (for chief-admin), then stored
      const urlClientId = searchParams.get("client_id");
      const storedClientId = getStoredClientId();
      const clientId = urlClientId || storedClientId;

      if (!clientId) {
        console.error(
          "TestCaseManagementPage: No client_id available for loading project structure",
        );
        setLoadingProjectStructure(false);
        return;
      }

      // Normalize DB boolean (true, 'true', 't', 1) for project settings
      const toBool = (v) =>
        v === true || v === "true" || v === "t" || v === 1 || v === "1";

      // Fetch current project by ID so we get full settings (ai_test_case_generation, test_case_approval)
      // regardless of list pagination; fallback to list for structure if get fails
      let project = null;
      try {
        const projectResponse = await projectService.getProject(
          projectId,
          clientId,
        );
        if (projectResponse?.success && projectResponse?.data) {
          project = projectResponse.data;
        }
      } catch (e) {
        logger.warn(e, "Could not load single project for settings");
      }
      if (!project) {
        const response = await projectService.getProjects({
          client_id: clientId,
          limit: 500,
        });
        const projects = response?.data?.projects || response?.projects || [];
        project = projects.find((p) => p.project_id === projectId) || null;
      }
      const structure = project?.structure || "SINGLE_REPOSITORY";
      const projectType = project?.project_type || project?.type;

      // Store project feature toggles (from project create: "Test case creation with AI")
      // Use toBool so PostgreSQL boolean 't' and any truthy representation is recognized
      setProjectSettings({
        ai_test_case_generation: toBool(project?.ai_test_case_generation),
        test_case_approval: toBool(project?.test_case_approval),
      });

      // Check if project is Multi Suite type
      const isMultiSuite =
        projectType === "MULTI_SUITE" ||
        projectType === "MULTIPLE_TEST_SUITES" ||
        projectType === "Multiple Test Suites to manage cases" ||
        structure === "MULTI_SUITE" ||
        structure === "MULTIPLE_TEST_SUITES";

      // For Multi Suite projects, require suite_id
      if (isMultiSuite) {
        let suiteId = searchParams.get("suite_id");

        // If suite_id is not in URL, try to get it from sessionStorage (for browser back navigation)
        if (!suiteId) {
          const storedSuiteId = sessionStorage.getItem(
            `testCaseCreate_suiteId_${projectId}`,
          );
          if (storedSuiteId) {
            // Restore suite_id to URL
            const newParams = new URLSearchParams(searchParams);
            newParams.set("suite_id", storedSuiteId);
            navigate(`/test-cases?${newParams.toString()}`, {
              replace: true,
            });
            suiteId = storedSuiteId;
          }
        }

        if (!suiteId) {
          // Redirect to suites page if no suite is selected
          const projectName =
            searchParams.get("project_name") || project?.name || "";
          toast.error("Please select a suite to view test cases");
          navigate(
            `/suites?project_id=${projectId}&project_name=${encodeURIComponent(projectName)}&client_id=${clientId}`,
          );
          setLoadingProjectStructure(false);
          return;
        }
      }

      setProjectStructure(structure);
    } catch (error) {
      logger.error(error, "Failed to load project structure");
      setProjectStructure("SINGLE_REPOSITORY"); // Default fallback
    } finally {
      setLoadingProjectStructure(false);
    }
  }, [projectId, searchParams, navigate]);

  // Check if project has test cases (for empty state detection)
  const checkTestCases = useCallback(async () => {
    if (!projectId) return;

    // Only check if we are still checking
    // if (!checkingTestCases) return;

    try {
      // Permission check: View Test Cases
      // We can reuse testcaseService.getTestCases but with limit 1
      // If user cannot view test cases, we might assume empty or handle error
      // Ideally use a lightweight API if available, but getTestCases(limit:1) is fine

      const response = await testcaseService.getTestCases(projectId, {
        limit: 1,
        page: 1,
      });

      const cases = response?.data?.test_cases || response?.test_cases || [];
      const hasCases = cases.length > 0;

      setHasTestCases(hasCases);
    } catch (error) {
      // If error (e.g. 403), assume no test cases accessible or handle gracefully
      setHasTestCases(false);
    } finally {
      setCheckingTestCases(false);
    }
  }, [projectId]);

  // Fetch ALL test cases once (section_id = null) for the initial "all sections" view
  const fetchAllTestCases = useCallback(async () => {
    if (!projectId) return;
    setGroupedTestCasesLoading(true);
    try {
      const response = await testcaseService.getTestCases(projectId, {
        section_id: null,
        page: 1,
        limit: 1000,
        branch_id: branchId || undefined,
      });
      const flat = response?.data?.test_cases || response?.test_cases || [];
      setAllTestCasesFlat(flat);
      setHasTestCases(flat.length > 0);
      setCheckingTestCases(false);
    } catch (error) {
      logger.error(error, "Failed to fetch all test cases");
      setAllTestCasesFlat([]);
      setCheckingTestCases(false);
    } finally {
      setGroupedTestCasesLoading(false);
    }
  }, [projectId, branchId]);

  // Separate function for fetching sections to be used in Promise.all and standalone
  const fetchSectionStructure = useCallback(async () => {
    try {
      const response = await sectionService.getSectionTree(
        projectId,
        branchId,
        suiteId,
        baselineId,
      );

      // Handle response structure - Backend returns { sections: [...] } inside response.data
      // sectionService.getSectionTree returns response.data, so response = { sections: [...] }
      // Check multiple possible response formats for backward compatibility
      const foldersFromAPI =
        response?.sections ||
        response?.data?.sections ||
        response?.data?.folders ||
        response?.folders ||
        (Array.isArray(response) ? response : []);

      // Map folder_id to section_id and parent_folder_id to parent_section_id for internal use
      const mapFolderToSection = (folder) => {
        const section = {
          ...folder,
          section_id: folder.section_id || folder.folder_id,
          parent_section_id:
            folder.parent_section_id !== undefined
              ? folder.parent_section_id
              : folder.parent_folder_id,
          children: folder.children
            ? folder.children.map(mapFolderToSection)
            : undefined,
        };
        return section;
      };

      const sections = foldersFromAPI.map(mapFolderToSection);

      // No root section logic - treat all sections equally
      // For MULTIPLE_TEST_SUITES, all top-level sections (parent_section_id = null) are suites
      // For other structures, all sections are treated equally - no special root handling
      if (
        projectStructure === "MULTIPLE_TEST_SUITES" ||
        projectStructure === "MULTI_SUITE"
      ) {
        setRootSectionName("All Test Cases");
        setCurrentSection("root");
        setRootSectionId("root");
      } else {
        setRootSectionId("root");
        setCurrentSection("root");

        setRootSectionName("All Test Cases");
      }

      // Set section structure directly - no root section extraction
      setSectionStructure(sections);
      setFullSectionStructure({ children: sections });
      setError(null);
    } catch (error) {
      const errorMessage = "Failed to fetch section structure";
      setError(errorMessage);
      logger.error(error, errorMessage);
      toast.error(errorMessage);
      setSectionStructure([]);
    }
    // Note: setSectionLoading(false) and setLoading(false) are handled in initPageData when called from there
    // But if called standalone (e.g. refresh), we might need to handle loading state manually if desired
  }, [
    projectId,
    branchId,
    suiteId,
    baselineId,
    projectStructure,
    setRootSectionName,
    setRootSectionId,
    setSectionStructure,
    setFullSectionStructure,
    setError,
  ]);

  // Fetch section structure and check test cases
  useEffect(() => {
    const initPageData = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      // Reset states on project change
      setSectionLoading(true);
      setCheckingTestCases(true);

      try {
        // Run both checks in parallel for stronger loading gate
        await Promise.all([
          fetchSectionStructure(),
          fetchAllTestCases(), // Fetch all test cases upfront with section_id=null
        ]);

        if (!canViewTestCases) {
          setCheckingTestCases(false);
        }
      } catch (error) {
        logger.error(error, "Failed to initialize page data");
      } finally {
        // Only turn off main loading when EVERYTHING is done
        setSectionLoading(false);
        setLoading(false);
      }
    };

    initPageData();
    loadProjectStructure();
  }, [
    projectId,
    refreshTrigger,
    loadProjectStructure,
    checkTestCases,
    canViewTestCases,
    fetchSectionStructure,
    fetchAllTestCases,
  ]);

  const fetchProjects = useCallback(async () => {
    const urlClientId = searchParams.get("client_id");
    const storedClientId = getStoredClientId();
    // Use URL client_id first (for chief-admin), then stored, then fallback
    const effectiveClientId = urlClientId || storedClientId;

    if (!effectiveClientId) {
      console.warn("TestCaseManagementPage: No client_id available");
      return;
    }

    const response = await projectService.getProjects({
      client_id: effectiveClientId,
      limit: 1000, // Load all projects for the dropdown
      page: 1,
    });
    const projects = response?.data?.projects;

    dispatch(setProjectsData(projects));
  }, [searchParams, dispatch]);
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects, projectId]);

  useEffect(() => {
    hasInitializedRef.current = false;
    hasAutoSelectedRef.current = false; // Reset auto-selection flag when project changes
    setRootSectionId(null);
    setSectionStructure([]);
    setCurrentSection(null);
  }, [projectId]);
  // Fetch distinct tags and users from current project test cases
  useEffect(() => {
    const fetchProjectFilters = async () => {
      if (!projectId) return;
      try {
        setUsersLoading(true);
        // Fetch a large number of test cases to get all distinct tags and users
        const response = await testcaseService.getTestCases(projectId, {
          page: 1,
          limit: 100,
          branch_id: branchId || undefined,
        });
        const testCases =
          response?.data?.test_cases || response?.test_cases || [];

        // Extract distinct tags
        const allTags = new Set();
        const userMap = new Map(); // Map user_id to user info

        testCases.forEach((tc) => {
          // Extract tags
          if (tc.tags && Array.isArray(tc.tags)) {
            tc.tags.forEach((tag) => allTags.add(tag));
          }

          // Extract distinct users who created test cases
          if (tc.created_by && tc.created_by_name) {
            if (!userMap.has(tc.created_by)) {
              userMap.set(tc.created_by, {
                user_id: tc.created_by,
                full_name: tc.created_by_name,
              });
            }
          }
        });

        setAvailableTags(Array.from(allTags).sort());
        setProjectUsers(
          Array.from(userMap.values()).sort((a, b) =>
            (a.full_name || "").localeCompare(b.full_name || ""),
          ),
        );
      } catch (error) {
        logger.error(error, "Failed to fetch project filters");
      } finally {
        setUsersLoading(false);
      }
    };
    fetchProjectFilters();
  }, [projectId, refreshTrigger]);

  // Refresh grouped test cases when refreshTrigger or filters change (if grouped display is active)
  // Skip the initial render to avoid double fetching
  const prevRefreshTrigger = React.useRef(0);
  useEffect(() => {
    if (
      currentSection &&
      fullSectionStructure &&
      groupedTestCases &&
      refreshTrigger > prevRefreshTrigger.current
    ) {
      // Only refresh if refreshTrigger actually changed and we're showing grouped test cases
      fetchGroupedTestCases(currentSection);
    }
    prevRefreshTrigger.current = refreshTrigger;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger, filters]);

  // Refresh grouped test cases when section structure is updated
  useEffect(() => {
    if (currentSection && fullSectionStructure && groupedTestCases) {
      fetchGroupedTestCases(currentSection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullSectionStructure]);

  // currentSection stays null until user clicks a section in the tree
  const hasInitializedRef = React.useRef(false);

  const handleCreateSection = async (parentSectionId = null) => {
    // No root section logic - parentSectionId is passed as-is (can be null for top-level sections)
    setEditingSection(
      parentSectionId ? { parent_section_id: parentSectionId } : null,
    );
    setShowSectionModal(true);
  };

  const handleEditSection = (section) => {
    setEditingSection(section);
    setShowSectionModal(true);
  };

  const handleSectionSubmit = async (sectionData) => {
    try {
      // No root section validation - parent_section_id can be null for top-level sections
      const wasCreating = !editingSection || !editingSection.section_id;
      const actionToResume = pendingAction;
      let targetSectionId = currentSection;

      if (editingSection && editingSection.section_id) {
        // Update existing section
        await sectionService.updateSection(
          editingSection.section_id,
          sectionData,
        );
        toast.success("Section updated successfully!");
        if (currentSection === editingSection.section_id) {
          targetSectionId = editingSection.section_id;
        }
      } else {
        // Create new section
        let createdSectionId;

        if (sectionData.subsectionName || sectionData.targetLocation) {
          // Handle first-time flow with potential subsection creation
          const result = await sectionService.createSectionWithSubsection({
            project_id: projectId,
            ...sectionData,
            parent_section_id: sectionData.parent_section_id || null,
            branch_id: branchId || null,
            baseline_id: searchParams.get("baseline_id") || null,
            suite_id: suiteId || null,
          });

          // Use the target section ID based on user's choice
          createdSectionId = result.targetSectionId;

          toast.success(
            result.subsection
              ? "Section and subsection created successfully!"
              : "Section created successfully!",
          );
        } else {
          // Standard section creation
          const { subsectionName, targetLocation, ...apiSectionData } =
            sectionData;

          const parentResponse = await sectionService.createSection({
            project_id: projectId,
            ...apiSectionData,
            parent_section_id: apiSectionData.parent_section_id || null,
            branch_id: branchId || null,
            baseline_id: searchParams.get("baseline_id") || null,
            suite_id: suiteId || null,
          });

          // Robust ID extraction handle various potential response structures
          createdSectionId =
            parentResponse?.section_id ||
            parentResponse?.data?.section_id ||
            parentResponse?.id ||
            parentResponse?.data?.id;

          toast.success("Section created successfully!");
        }

        if (createdSectionId) {
          targetSectionId = createdSectionId;
        }
      }

      // Refresh section structure
      setRefreshTrigger((prev) => prev + 1);
      setShowSectionModal(false);
      setEditingSection(null);

      // Handle post-creation actions
      if (wasCreating && actionToResume && targetSectionId) {
        // Store recently created section for auto-selection
        sessionStorage.setItem(
          `recentlyCreatedSection_${projectId}`,
          targetSectionId,
        );

        // Clear pending action first
        setPendingAction(null);

        // Auto-select the newly created section
        setCurrentSection(targetSectionId);

        // Small delay to ensure section structure is updated
        setTimeout(() => {
          if (actionToResume === "create_test_case") {
            // Navigate to test case creation with the new section pre-selected
            const params = new URLSearchParams(searchParams);
            params.set("section_id", targetSectionId);
            navigate(`/test-cases/create?${params.toString()}`);
          } else if (actionToResume === "generate_ai") {
            // Open AI generation modal with the new section
            setShowAIModal(true);
          }
        }, 300);
      } else if (wasCreating && targetSectionId) {
        // Store recently created section for auto-selection even if no pending action
        sessionStorage.setItem(
          `recentlyCreatedSection_${projectId}`,
          targetSectionId,
        );

        // Auto-select the newly created section even if no pending action
        setCurrentSection(targetSectionId);
      }
    } catch (error) {
      console.error("Section submission error:", error);
      toast.error(error.message || "Failed to save section");
    }
  };

  const handleDeleteSection = (section) => {
    if (!section) {
      toast.error("Section not found.");
      return;
    }
    setSectionToDelete(section);
    setShowDeleteSectionModal(true);
  };

  const handleConfirmDeleteSection = async (section, options) => {
    try {
      await sectionService.deleteSection(section.section_id, options);
      toast.success("Section deleted successfully!");

      // If deleted section was selected, clear selection
      if (currentSection === section.section_id) {
        setCurrentSection(null);
      }

      setRefreshTrigger((prev) => prev + 1);
      setShowDeleteSectionModal(false);
      setSectionToDelete(null);
    } catch (error) {
      console.error("[Delete Section] Error:", error);
      logger.error(error, "Failed to delete section");
      toast.error(error.message || "Failed to delete section");
      throw error; // Re-throw so modal can handle it
    }
  };

  // Get test case count for a section
  const getSectionTestCaseCount = useCallback(
    (sectionId) => {
      if (!sectionId) return 0;

      // First try to get from groupedTestCases
      if (groupedTestCases && groupedTestCases[sectionId]) {
        return groupedTestCases[sectionId].length;
      }

      // Try to get from section object itself (from backend)
      const findSection = (sections, id) => {
        for (const section of sections || []) {
          if (section.section_id === id) {
            return section;
          }
          if (section.children) {
            const found = findSection(section.children, id);
            if (found) return found;
          }
        }
        return null;
      };

      const section =
        findSection(sectionStructure, sectionId) ||
        (fullSectionStructure
          ? findSection(fullSectionStructure.children || [], sectionId)
          : null);

      if (section && section.test_cases_count !== undefined) {
        return parseInt(section.test_cases_count, 10) || 0;
      }

      return 0;
    },
    [groupedTestCases, sectionStructure, fullSectionStructure],
  );

  // Get subsection count for a section
  const getSubsectionCount = useCallback(
    (sectionId) => {
      if (!sectionId) return 0;

      const findSection = (sections, id) => {
        for (const section of sections || []) {
          if (section.section_id === id) {
            return section;
          }
          if (section.children) {
            const found = findSection(section.children, id);
            if (found) return found;
          }
        }
        return null;
      };

      const section =
        findSection(sectionStructure, sectionId) ||
        (fullSectionStructure
          ? findSection(fullSectionStructure.children || [], sectionId)
          : null);

      if (section && section.children && Array.isArray(section.children)) {
        return section.children.length;
      }

      return 0;
    },
    [sectionStructure, fullSectionStructure],
  );

  // Helper function to recursively get all subsection IDs
  const getAllSubsectionIds = (section, sectionId) => {
    if (!section || section.section_id !== sectionId) {
      // Search in children
      if (section?.children) {
        for (const child of section.children) {
          const result = getAllSubsectionIds(child, sectionId);
          if (result) return result;
        }
      }
      return null;
    }

    // Found the section, collect all subsection IDs
    const subsectionIds = [];
    const collectSubsections = (f) => {
      if (f.children && f.children.length > 0) {
        f.children.forEach((child) => {
          subsectionIds.push(child.section_id);
          collectSubsections(child);
        });
      }
    };
    collectSubsections(section);
    return subsectionIds;
  };

  // Helper function to find section by ID in the structure
  const findSectionById = (section, sectionId) => {
    if (section.section_id === sectionId) {
      return section;
    }
    if (section.children) {
      for (const child of section.children) {
        const found = findSectionById(child, sectionId);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper function to get section name by ID
  const getSectionName = (sectionId) => {
    if (!sectionId) {
      return "All Test Cases";
    }
    if (fullSectionStructure) {
      const section = findSectionById(fullSectionStructure, sectionId);
      return section?.name || section?.display_name || "Unknown Section";
    }
    return "Unknown Section";
  };

  // Fetch grouped test cases for parent section
  const fetchGroupedTestCases = async (parentSectionId) => {
    const isMultiSuite =
      projectStructure === "MULTIPLE_TEST_SUITES" ||
      projectStructure === "MULTI_SUITE";

    // For MULTIPLE_TEST_SUITES, null means "All Test Cases" - fetch from all suites
    // For other structures, null means no section selected - don't fetch grouped
    if (!isMultiSuite && (!parentSectionId || !fullSectionStructure)) {
      setGroupedTestCases(null);
      return;
    }

    // For MULTIPLE_TEST_SUITES with null parentSectionId, we need sectionStructure
    if (isMultiSuite && parentSectionId === null && !sectionStructure) {
      setGroupedTestCases(null);
      return;
    }

    setGroupedTestCasesLoading(true);
    try {
      // For MULTIPLE_TEST_SUITES with null parentSectionId, treat as "All Test Cases"
      // For other structures, null means "All Test Cases"
      const isRootSection = parentSectionId === null;

      let parentSection;
      let allSectionIds = [];

      // Helper function to get all section IDs recursively
      const getAllSectionIds = (sections) => {
        let ids = [];
        sections.forEach((section) => {
          if (section.section_id) {
            ids.push(section.section_id);
          }
          if (section.children && section.children.length > 0) {
            ids = ids.concat(getAllSectionIds(section.children));
          }
        });
        return ids;
      };

      if (isMultiSuite && parentSectionId === null) {
        // For MULTIPLE_TEST_SUITES "All Test Cases", get all suites and sections
        if (sectionStructure && sectionStructure.length > 0) {
          allSectionIds = getAllSectionIds(sectionStructure);
        }
        // No parent section for "All Test Cases" - we'll group by suite
        parentSection = null;
      } else if (isRootSection && !isMultiSuite) {
        // For root section in other structures, use fullSectionStructure directly
        parentSection = fullSectionStructure;
        if (parentSection.children && parentSection.children.length > 0) {
          allSectionIds = getAllSectionIds(parentSection.children);
        } else if (sectionStructure && sectionStructure.length > 0) {
          allSectionIds = getAllSectionIds(sectionStructure);
        }
      } else {
        // For specific section, find it in the structure
        parentSection = findSectionById(fullSectionStructure, parentSectionId);
        if (!parentSection) {
          setGroupedTestCases(null);
          setGroupedTestCasesLoading(false);
          return;
        }
        // Get subsections recursively
        if (parentSection.children && parentSection.children.length > 0) {
          allSectionIds = getAllSubsectionIds(
            fullSectionStructure,
            parentSectionId,
          );
        }
      }

      // If no sections at all, use regular display
      if (allSectionIds.length === 0 && !isMultiSuite) {
        setGroupedTestCases(null);
        setGroupedTestCasesLoading(false);
        return;
      }

      // Build filter params
      const filterParams = {};
      if (filters.status) filterParams.status = filters.status;
      if (filters.tags && filters.tags.length > 0)
        filterParams.tags = filters.tags;
      if (filters.created_by) filterParams.created_by = filters.created_by;
      if (filters.assignedTo && filters.assignedTo !== "all")
        filterParams.assignedTo = filters.assignedTo;

      let parentTestCases = [];

      // For MULTIPLE_TEST_SUITES "All Test Cases", there's no parent section
      // For other structures, fetch test cases from parent section
      if (parentSectionId !== null) {
        const parentResponse = await testcaseService.getTestCases(projectId, {
          section_id: parentSectionId,
          page: 1,
          limit: 100, // Get all test cases
          branch_id: branchId || undefined,
          ...filterParams,
        });
        parentTestCases =
          parentResponse?.data?.test_cases || parentResponse?.test_cases || [];
      }

      // Fetch test cases from each section (suite/section)
      const sectionPromises = allSectionIds.map((sectionId) =>
        testcaseService.getTestCases(projectId, {
          section_id: sectionId,
          page: 1,
          limit: 100,
          branch_id: branchId || undefined,
          ...filterParams,
        }),
      );

      const sectionResponses = await Promise.all(sectionPromises);

      // Group test cases by section
      const grouped = {
        parent:
          isMultiSuite && parentSectionId === null
            ? null
            : {
                sectionId: parentSectionId,
                sectionName: getSectionName(parentSectionId),
                testCases: parentTestCases,
              },
        subsections: allSectionIds.map((sectionId, index) => {
          const response = sectionResponses[index];
          const testCases =
            response?.data?.test_cases || response?.test_cases || [];
          return {
            sectionId: sectionId,
            sectionName: getSectionName(sectionId),
            testCases: testCases,
          };
        }),
      };

      // CORE STATE-AWARE LOGIC: Only set groupedTestCases if there are actual test cases
      // Check if any test cases exist in the grouped structure
      const hasParentTestCases =
        grouped.parent &&
        grouped.parent.testCases &&
        grouped.parent.testCases.length > 0;
      const hasSubsectionTestCases = grouped.subsections.some(
        (sub) => sub.testCases && sub.testCases.length > 0,
      );
      const hasAnyTestCases = hasParentTestCases || hasSubsectionTestCases;

      // STATE RULE: If sections exist but no test cases, don't set groupedTestCases
      // This allows the empty state to show instead
      if (!hasAnyTestCases) {
        // No test cases - let empty state handle the display
        setGroupedTestCases(null);
        setGroupedTestCasesLoading(false);
        return;
      }

      // Only set groupedTestCases if there are actual test cases to display
      // For "All Test Cases" in MULTIPLE_TEST_SUITES, show grouped view if there are any suites with test cases
      // For root section in other structures, show grouped view if there are any sections with test cases
      if (
        (isMultiSuite &&
          parentSectionId === null &&
          allSectionIds.length > 0) ||
        (isRootSection && !isMultiSuite && allSectionIds.length > 0)
      ) {
        // Show grouped view for root/"All Test Cases" (only if test cases exist)
        setGroupedTestCases(grouped);
      } else if (grouped.subsections.length > 0) {
        // Other sections: show grouped view only if there are subsections with test cases
        setGroupedTestCases(grouped);
      } else {
        // No sections or no test cases, use regular display
        setGroupedTestCases(null);
      }
    } catch (error) {
      logger.error(error, "Failed to fetch grouped test cases");
      toast.error("Failed to load test cases");
      setGroupedTestCases(null);
    } finally {
      setGroupedTestCasesLoading(false);
    }
  };

  const handleSectionSelect = (sectionId) => {
    const effectiveSectionId =
      sectionId === "ROOT" || sectionId === null ? null : sectionId;
    setCurrentSection(effectiveSectionId);
  };

  const handleMoveTestCase = async (testCaseId, targetSectionId) => {
    try {
      // targetSectionId can be 'ROOT' (which means null/show all), or an actual section_id
      // Since we don't have root sections, 'ROOT' means don't filter by section (null)
      const sectionId = targetSectionId === "ROOT" ? null : targetSectionId;

      // If sectionId is null, we can't move to "all test cases" - user must select a section
      if (sectionId === null) {
        toast.error(
          "Please select a specific section to move the test case to.",
        );
        return;
      }

      await sectionService.moveTestCasesToSection([testCaseId], sectionId);
      toast.success("Test case moved successfully!");
      setRefreshTrigger((prev) => prev + 1);
      // Refresh grouped test cases if they are being displayed
      if (groupedTestCases) {
        await fetchGroupedTestCases(currentSection);
      }
    } catch (error) {
      logger.error(error, "Failed to move test case");
      toast.error(error.message || "Failed to move test case");
    }
  };

  // Check if sections exist - no root section logic
  // CORE STATE-AWARE: Returns true if ANY sections exist (regardless of test cases)
  const checkSectionsExist = () => {
    if (!Array.isArray(sectionStructure)) return false;
    if (sectionStructure.length === 0) return false;

    // Flatten sections to check total count (including nested children)
    const flattenSections = (sectionList) => {
      let count = 0;
      sectionList.forEach((section) => {
        if (section && section.section_id) {
          count++;
          if (
            section.children &&
            Array.isArray(section.children) &&
            section.children.length > 0
          ) {
            count += flattenSections(section.children); // Fixed: removed .length
          }
        }
      });
      return count;
    };
    const totalCount = flattenSections(sectionStructure);

    return totalCount > 0;
  };

  const handleCreateTestCase = (sectionId = null) => {
    // Check if sections exist before allowing test case creation
    if (!checkSectionsExist()) {
      // No sections exist - trigger section creation flow first
      setPendingAction("create_test_case");
      setEditingSection(null);
      setShowSectionModal(true);
      return;
    }

    // Sections exist - navigate to full-page create view
    const params = new URLSearchParams({ project_id: projectId });

    // Prioritize passed sectionId, then currentSection
    const targetSectionId = sectionId || currentSection;
    if (targetSectionId) {
      params.append("section_id", targetSectionId);
    }

    // Preserve branch_id from URL if it exists
    const branchId = searchParams.get("branch_id");
    if (branchId) {
      params.append("branch_id", branchId);
    }
    // Preserve suite_id from URL if it exists (for MULTIPLE_TEST_SUITES)
    if (suiteId) {
      params.append("suite_id", suiteId);
    }
    // Preserve other important params
    const projectName = searchParams.get("project_name");
    const clientId = searchParams.get("client_id");
    if (projectName) {
      params.append("project_name", projectName);
    }
    if (clientId) {
      params.append("client_id", clientId);
    }
    navigate(`/test-cases/create?${params.toString()}`);
  };

  const handleGenerateAI = () => {
    // Check if sections exist before allowing AI generation
    if (!checkSectionsExist()) {
      // No sections exist - trigger section creation flow first
      setPendingAction("generate_ai");
      setEditingSection(null);
      setShowSectionModal(true);
      return;
    }

    // Sections exist - proceed normally
    setShowAIModal(true);
  };

  // handleCreateSection is already defined above (line 335)

  // Check if project has any test cases
  const checkTestCasesExist = useCallback(async () => {
    if (!projectId) {
      setCheckingTestCases(false);
      return;
    }

    try {
      setCheckingTestCases(true);
      const response = await testcaseService.getTestCases(projectId, {
        page: 1,
        limit: 1,
        branch_id: branchId || undefined,
      });
      const testCases =
        response?.data?.test_cases || response?.test_cases || [];
      setHasTestCases(testCases.length > 0);
    } catch (error) {
      logger.error(error, "Failed to check test cases");
      setHasTestCases(false);
    } finally {
      setCheckingTestCases(false);
    }
  }, [projectId]);

  // Check test cases when projectId changes or after refresh
  useEffect(() => {
    checkTestCasesExist();
  }, [checkTestCasesExist, refreshTrigger]);

  const handleAITestCasesGenerated = async (testCases, metadata) => {
    // Show preview modal instead of directly creating test cases
    setPreviewTestCases(testCases);
    setPreviewMetadata(metadata);
    setShowPreviewModal(true);
  };

  const handlePreviewConfirm = async (selectedTestCases, metadata) => {
    setCreatingAITestCases(true);
    const sectionId = metadata.sectionId || currentSection;
    const reviewerId = metadata.reviewerId || null;

    let successCount = 0;
    let errorCount = 0;

    try {
      // Convert selected test cases object to array
      const testCaseEntries = Object.entries(selectedTestCases);

      for (const [testCaseId, testCaseData] of testCaseEntries) {
        try {
          // Map AI response format to our test case format
          const testSteps = parseTestSteps(testCaseData["Test Steps"] || "");
          // Prioritize Test Scenario as title, fallback to Test Case ID, then testCaseId
          const testCaseTitle =
            testCaseData["Test Scenario"] ||
            testCaseData["Test Case ID"] ||
            testCaseId;
          const testCaseExpectedResult = testCaseData["Expected Result"] || "";

          // Determine status:
          // - If reviewer is assigned, default to 'review' (unless explicitly overridden in preview)
          // - Use status from test case data if set, otherwise default based on reviewer
          let finalStatus = testCaseData["Status"];
          if (!finalStatus) {
            // No status set in preview, default based on reviewer
            finalStatus = reviewerId ? "review" : "active";
          }
          // If reviewer is assigned but status was explicitly set to something other than 'review',
          // still use 'review' to ensure proper review workflow
          if (reviewerId && finalStatus !== "review") {
            finalStatus = "review";
          }

          // Preserve branch_id from URL if it exists
          const branchId = searchParams.get("branch_id");

          const testCasePayload = {
            title: testCaseTitle,
            description: testCaseData["Test Scenario"] || "",
            preconditions: testCaseData["Precondition"] || "",
            expected_result: testCaseExpectedResult,
            priority: mapPriority(testCaseData["Priority"] || "medium"),
            status: finalStatus,
            section_id: sectionId, // Required: test cases must belong to a section
            folder_id: sectionId, // Backward compatibility
            reviewer_id: reviewerId, // Include reviewer_id from metadata
            branch_id: branchId || null, // Preserve branch_id from URL
            testSteps: testSteps,
          };

          // Create test case
          const response = await testcaseService.createTestCase(
            projectId,
            testCasePayload,
          );
          const createdTestCaseId = response.data.test_case_id;

          // Create a single test step combining all steps with the test case's expected result
          if (testSteps && testSteps.length > 0) {
            // Combine all steps into a single action
            const combinedAction = testSteps
              .filter((step) => step.step.trim() !== "")
              .map((step, index) => `${index + 1}. ${step.step.trim()}`)
              .join("\n");

            // Only create step if there's content
            if (combinedAction.trim().length > 0) {
              const singleStepData = [
                {
                  step_number: 1,
                  action: combinedAction,
                  expected_result: testCaseExpectedResult, // Use test case's expected result
                  status: "pending",
                },
              ];

              await teststepsService.bulkCreateTestSteps(
                createdTestCaseId,
                singleStepData,
              );
            }
          }

          successCount++;
        } catch (error) {
          console.error(`Error creating test case ${testCaseId}:`, error);
          logger.error(
            error,
            `Failed to create AI-generated test case ${testCaseId}`,
          );
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(
          `Successfully created ${successCount} test case(s)${errorCount > 0 ? ` (${errorCount} failed)` : ""}`,
        );
        setHasTestCases(true); // Update state since test cases now exist
        setRefreshTrigger((prev) => prev + 1);
        // Refresh grouped test cases if they are being displayed
        if (groupedTestCases) {
          await fetchGroupedTestCases(currentSection);
        }
      } else {
        toast.error("Failed to create test cases");
      }
    } catch (error) {
      logger.error(error, "Failed to create AI-generated test cases");
      toast.error("Failed to create test cases");
    } finally {
      setCreatingAITestCases(false);
      setShowPreviewModal(false);
      setPreviewTestCases(null);
      setPreviewMetadata(null);
    }
  };

  const mapPriority = (priority) => {
    if (!priority) return "medium";
    const lowerPriority = priority.toLowerCase();
    if (lowerPriority.includes("critical") || lowerPriority.includes("high"))
      return "high";
    if (lowerPriority.includes("low")) return "low";
    return "medium";
  };

  const parseTestSteps = (testStepsString) => {
    if (!testStepsString) return [];

    // Try to parse as numbered list or bullet points
    const steps = testStepsString
      .split(/\n|\r\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => {
        // Remove numbering (1., 2., etc.) or bullets (-, *, etc.)
        const cleaned = line
          .replace(/^[\d]+[.)]\s*/, "")
          .replace(/^[-*]\s*/, "");
        return {
          step: cleaned,
          expectedResult: "",
        };
      });

    return steps.length > 0 ? steps : [{ step: "", expectedResult: "" }];
  };

  const handleTestCaseSubmit = async (testCaseData) => {
    try {
      const isUpdate = editingTestCase && editingTestCase.test_case_id;

      if (isUpdate) {
        // Update existing test case
        const testCaseId = editingTestCase.test_case_id;

        // Include steps in update payload for validation
        const updatePayload = {
          title: testCaseData.title,
          description: testCaseData.description,
          preconditions: testCaseData.preconditions,
          expected_result: testCaseData.expected_result,
          priority: testCaseData.priority,
          status: testCaseData.status,
          template_id: testCaseData.template_id,
          owner: testCaseData.owner,
          reviewer_id: testCaseData.reviewer_id || null,
          state: testCaseData.state,
          type_of_test_case: testCaseData.type_of_test_case,
          automation_status: testCaseData.automation_status,
          tags: testCaseData.tags,
          attachments: testCaseData.attachments,
          steps:
            testCaseData.steps ||
            (testCaseData.testSteps && testCaseData.testSteps.length > 0
              ? testCaseData.testSteps
                  .filter((step) => step.step.trim() !== "")
                  .map((step, index) => ({
                    step_number: index + 1,
                    action: step.step,
                    expected_result: step.expectedResult || "",
                    gherkin_keyword: step.gherkin_keyword || null,
                  }))
              : []),
        };

        await testcaseService.updateTestCase(
          projectId,
          testCaseId,
          updatePayload,
        );

        // Steps are now updated by the backend service based on template type
        // No need to delete and recreate separately

        setRefreshTrigger((prev) => prev + 1);
        // Refresh grouped test cases if they are being displayed
        if (groupedTestCases) {
          await fetchGroupedTestCases(currentSection);
        }
        toast.success("Test case updated successfully!");
        logger.info("Test case updated successfully");
      } else {
        // Create new test case
        // section_id and template_id are already included in testCaseData from the form

        // Include template type and steps in the payload for validation
        // Map section_id to folder_id for API compatibility
        const { section_id, ...restData } = testCaseData;
        const createPayload = {
          ...restData,
          folder_id: section_id, // Map section_id to folder_id for API
          // Ensure suite_id is included for MULTIPLE_TEST_SUITES projects
          suite_id:
            projectStructure === "MULTIPLE_TEST_SUITES" ||
            projectStructure === "MULTI_SUITE"
              ? testCaseData.suite_id || suiteId || null
              : testCaseData.suite_id || null,
          test_case_template_type:
            testCaseData.test_case_template_type || "MULTIPLE_STEPS",
          steps:
            testCaseData.testSteps && testCaseData.testSteps.length > 0
              ? testCaseData.testSteps
                  .filter((step) => step.step.trim() !== "") // Only include steps with content
                  .map((step, index) => ({
                    step_number: index + 1,
                    action: step.step,
                    expected_result: step.expectedResult || "",
                    gherkin_keyword: step.gherkin_keyword || null, // For BDD
                  }))
              : [],
        };

        await testcaseService.createTestCase(projectId, createPayload);

        // Steps are now created by the backend service based on template type
        // No need to create them separately
        // Comments are also handled by the backend createTestCase API

        setHasTestCases(true); // Update state since test cases now exist
        setRefreshTrigger((prev) => prev + 1);
        // Refresh grouped test cases if they are being displayed
        if (groupedTestCases) {
          await fetchGroupedTestCases(currentSection);
        }
        toast.success("Test case created successfully!");
        logger.info("Test case created successfully");
      }

      // Close modal and reset editing state
      setShowTestCaseModal(false);
      setEditingTestCase(null);
    } catch (error) {
      logger.error(
        error,
        `Failed to ${editingTestCase ? "update" : "create"} test case`,
      );
      toast.error(
        `Failed to ${editingTestCase ? "update" : "create"} test case`,
      );
      throw error;
    }
  };

  const handleViewTestCase = async (testCase) => {
    try {
      if (!testCase.test_case_id || !projectId) {
        toast.error("Test case ID or project ID is missing");
        return;
      }

      // Left chevron on active row signals close
      if (testCase.__closePanel) {
        handleCloseDetailPanel();
        return;
      }

      // Close history panel if open, open detail panel
      setShowHistoryPanel(false);
      setHistoryTestCaseId(null);
      setSelectedTestCaseId(testCase.test_case_id);
      setLastTestCaseForHistory(testCase.test_case_id);
      setDetailPanelViewMode(true);
      setShowDetailPanel(true);
      setActiveTestCaseId(testCase.test_case_id);
    } catch (error) {
      logger.error(error, "Failed to open test case view");
      toast.error("Failed to load test case details");
    }
  };

  const handleEditTestCase = async (testCase) => {
    try {
      if (!testCase.test_case_id || !projectId) {
        toast.error("Test case ID or project ID is missing");
        return;
      }

      setLastTestCaseForHistory(testCase.test_case_id);
      // Open inline detail panel in edit mode
      setShowHistoryPanel(false);
      setHistoryTestCaseId(null);
      setSelectedTestCaseId(testCase.test_case_id);
      setDetailPanelViewMode(false);
      setShowDetailPanel(true);
      setActiveTestCaseId(testCase.test_case_id);
    } catch (error) {
      logger.error(error, "Failed to open test case for editing");
      toast.error("Failed to load test case details");
    }
  };

  // Handle edit from detail panel - now handled inline, this is just a placeholder
  const handleEditFromPanel = (testCase) => {
    // Edit is now handled inline in the detail panel
    // This function is kept for compatibility but doesn't need to do anything
  };

  // Navigate to prev/next test case in the flat list
  const navigateTestCase = (direction) => {
    const currentId = selectedTestCaseId || historyTestCaseId;
    if (!currentId || allTestCasesFlat.length === 0) return;
    const idx = allTestCasesFlat.findIndex(
      (tc) => tc.test_case_id === currentId,
    );
    if (idx === -1) return;
    const nextIdx = direction === "prev" ? idx - 1 : idx + 1;
    if (nextIdx < 0 || nextIdx >= allTestCasesFlat.length) return;
    const nextTc = allTestCasesFlat[nextIdx];
    setActiveTestCaseId(nextTc.test_case_id);
    if (showDetailPanel) {
      setSelectedTestCaseId(nextTc.test_case_id);
      setLastTestCaseForHistory(nextTc.test_case_id);
    }
    if (showHistoryPanel) {
      setHistoryTestCaseId(nextTc.test_case_id);
      setLastTestCaseForHistory(nextTc.test_case_id);
    }
  };

  // Handle panel close
  const handleCloseDetailPanel = () => {
    setShowDetailPanel(false);
    setSelectedTestCaseId(null);
    setActiveTestCaseId(null);
  };

  // Handle panel update (after save)
  const handlePanelUpdate = () => {
    // Refresh test cases
    setRefreshTrigger((prev) => prev + 1);
    if (currentSection) {
      fetchGroupedTestCases(currentSection);
    }
  };

  const handleDeleteTestCase = async (testCase) => {
    if (!testCase.test_case_id || !projectId) {
      toast.error("Test case ID or project ID is missing");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete test case "${testCase.title}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      await testcaseService.deleteTestCase(projectId, testCase.test_case_id);
      toast.success("Test case deleted successfully!");

      // Immediately update groupedTestCases state to remove the deleted test case
      if (groupedTestCases) {
        setGroupedTestCases((prev) => {
          if (!prev) return null;

          const updated = { ...prev };

          // Remove from parent test cases if it exists there
          if (updated.parent && updated.parent.testCases) {
            updated.parent = {
              ...updated.parent,
              testCases: updated.parent.testCases.filter(
                (tc) => tc.test_case_id !== testCase.test_case_id,
              ),
            };
          }

          // Remove from subsection test cases
          if (updated.subsections) {
            updated.subsections = updated.subsections.map((subsection) => ({
              ...subsection,
              testCases: subsection.testCases.filter(
                (tc) => tc.test_case_id !== testCase.test_case_id,
              ),
            }));
          }

          return updated;
        });
      }

      // Trigger refresh for other components that might be displaying test cases
      setRefreshTrigger((prev) => prev + 1);

      // Refresh grouped test cases if they are being displayed (as a fallback)
      if (groupedTestCases && currentSection) {
        // Use setTimeout to allow state update to complete first
        setTimeout(async () => {
          await fetchGroupedTestCases(currentSection);
        }, 100);
      }
    } catch (error) {
      logger.error(error, "Failed to delete test case");
      toast.error(error.message || "Failed to delete test case");
    }
  };

  const handleAssignTestCase = (testCase) => {
    // TODO: Implement assign test case functionality
    toast("Assign functionality coming soon");
    logger.info(
      "[TestCaseManagementPage] Assign test case:",
      testCase.test_case_id,
    );
  };

  const columns = [
    {
      field: "title",
      header: "Test Case Name",
      sortable: true,
      render: (title, row) => (
        <button
          draggable={true}
          onDragStart={(e) => {
            e.dataTransfer.setData("text/plain", row.test_case_id);
            e.dataTransfer.effectAllowed = "move";
            e.currentTarget.style.opacity = "0.5";
            e.currentTarget.style.cursor = "grabbing";
          }}
          onDragEnd={(e) => {
            e.currentTarget.style.opacity = "1";
            e.currentTarget.style.cursor = "pointer";
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleViewTestCase(row);
          }}
          className="text-indigo-600 hover:text-indigo-800 hover:underline text-left cursor-move"
          title="Drag to move test case to a section"
        >
          {title || "Untitled Test Case"}
        </button>
      ),
    },
    {
      field: "status",
      header: "Status",
      sortable: true,
      render: (status) => {
        const statusConfig = {
          approved: "bg-green-100 text-green-800",
          active: "bg-blue-100 text-blue-800",
          review: "bg-yellow-100 text-yellow-800",
          requested_changes: "bg-orange-100 text-orange-800",
          rejected: "bg-red-100 text-red-800",
          deprecated: "bg-bg-tertiary text-text-secondary",
          draft: "bg-bg-tertiary text-text-secondary",
        };
        const statusClass =
          statusConfig[status] || "bg-bg-tertiary text-text-secondary";
        const displayText =
          status === "requested_changes"
            ? "Requested Changes"
            : status
              ? status.charAt(0).toUpperCase() + status.slice(1)
              : "Draft";
        return (
          <span className={`px-2 py-1 rounded-full text-xs ${statusClass}`}>
            {displayText}
          </span>
        );
      },
    },
    {
      field: "priority",
      header: "Priority",
      sortable: true,
      render: (priority) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${
            priority === "critical"
              ? "bg-red-100 text-red-800"
              : priority === "high"
                ? "bg-orange-100 text-orange-800"
                : priority === "medium"
                  ? "bg-yellow-100 text-yellow-800"
                  : priority === "low"
                    ? "bg-green-100 text-green-800"
                    : "bg-bg-tertiary text-text-secondary"
          }`}
        >
          {priority
            ? priority.charAt(0).toUpperCase() + priority.slice(1)
            : "Medium"}
        </span>
      ),
    },
    {
      field: "tags",
      header: "Tags",
      sortable: false,
      render: (tags) => {
        if (!tags || !Array.isArray(tags) || tags.length === 0) {
          return <span className="text-text-tertiary text-xs">No tags</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {tags.map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 rounded-full text-xs bg-indigo-100 text-indigo-800"
              >
                {tag}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      field: "created_by_name",
      header: "Created By",
      sortable: true,
      render: (createdByName, row) => {
        return (
          <span className="text-sm text-text-primary">
            {createdByName || row.created_by || "Unknown"}
          </span>
        );
      },
    },
  ];

  // Add structure-specific columns conditionally
  const structureColumns = [];

  if (projectStructure === "MULTI_SUITE") {
    structureColumns.push({
      field: "suite_name",
      header: "Suite",
      sortable: true,
      render: (suiteName, row) => {
        if (!suiteName && !row.suite_id) {
          return <span className="text-text-tertiary text-xs">No suite</span>;
        }
        return (
          <span className="text-sm text-text-primary">
            {suiteName || row.suite_name || "Unknown Suite"}
          </span>
        );
      },
    });
  }

  // SINGLE REPOSITORY WITH BASELINE SUPPORT - commented out: Branch and Baseline columns
  // if (projectStructure === 'BASELINE_ENABLED' || projectStructure === 'SINGLE_REPOSITORY_WITH_BASELINE_AND_BRANCH') {
  //   structureColumns.push({ field: 'branch_name', header: 'Branch', ... });
  //   structureColumns.push({ field: 'baseline_name', header: 'Baseline', ... });
  // }

  if (loading) {
    return (
      <div className="min-h-screen p-4">
        <PageSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        {error}
      </div>
    );
  }

  // Check permission after all hooks are called
  if (!canViewTestCases) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-text-primary mb-2">
            Access Denied
          </h2>
          <p className="text-text-secondary">
            You do not have permission to view test cases.
          </p>
        </div>
      </div>
    );
  }

  // Determine if project is completely empty (no sections AND no test cases)
  const hasSections = checkSectionsExist();
  const isProjectEmpty = !hasSections && !hasTestCases && !checkingTestCases;

  return (
    <ErrorBoundary>
      <div
        className="w-full max-w-full min-w-0 overflow-x-hidden"
        style={{
          height: "calc(100vh - 80px)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          padding: "12px",
          boxSizing: "border-box",
        }}
      >
        <div
          className="flex-shrink-0 flex items-center justify-between border-b"
          style={{
            padding: "4px",
            minHeight: "64px",
          }}
        >
          {/* Page Heading - Left side */}
          <h1
            className="text-text-primary"
            style={{
              fontSize: "24px",
              fontWeight: 600,
              color: "#111827",
              margin: 0,
            }}
          >
            Test Cases
          </h1>

          {/* Action Buttons - Right side */}
          <div
            className="flex items-center"
            style={{ gap: "12px", flexWrap: "nowrap" }}
          >
            {/* Import Button - Outlined */}
            <button
              onClick={() => {
                navigate(
                  `/data-management?project_id=${projectId}&project_name=${encodeURIComponent(projectName)}&client_id=${clientId}&view_only=true`,
                );
              }}
              className="flex color: items-center bg-[#EEFDF3FF] font-medium border-none hover:bg-[#B8F5CDFF] transition-colors"
              style={{
                height: "38px",
                padding: "0 16px",
                borderRadius: "18px",
                gap: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#117B34FF",
                borderWidth: 0,
              }}
            >
              <Upload className="w-4 h-4" />
              <span>Import</span>
            </button>

            {/* Export Button - Outlined */}
            <button
              onClick={handleExportAction}
              disabled={exportStartLoading}
              className="flex items-center font-medium border-none bg-[#FDF2F2FF] hover:bg-[#F9DBDCFF] transition-colors"
              style={{
                height: "38px",
                padding: "0 16px",
                borderRadius: "18px",
                borderWidth: 0,
                gap: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#DE3B40FF",
              }}
            >
              <Download className="w-4 h-4" />
              <span>
                {!exportSelectionMode
                  ? "Export"
                  : exportStartLoading
                    ? "Exporting..."
                    : "Start Export"}
              </span>
            </button>
            {exportSelectionMode && (
              <>
                <button
                  onClick={() => {
                    setExportSelectionMode(false);
                    setSelectedTestCaseIdsForExport([]);
                  }}
                  className="flex items-center font-medium border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
                  style={{
                    height: "38px",
                    padding: "0 16px",
                    borderRadius: "18px",
                    gap: "8px",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#374151",
                  }}
                >
                  <Close className="w-4 h-4" />
                  <span>Cancel Export Mode</span>
                </button>
                <span className="text-sm text-gray-600">
                  {selectedTestCaseIdsForExport.length} selected
                </span>
              </>
            )}

            {/* Global Variables Button - Outlined */}
            <button
              onClick={() => {
                // TODO: Implement global variables functionality
                navigate(`/settings?tab=variables&section=customization`);
              }}
              className="flex items-center font-medium border-none bg-[ #F9F7F5FF] hover:bg-[#EEE7E3FF] transition-colors"
              style={{
                height: "38px",
                padding: "0 16px",
                borderRadius: "18px",
                gap: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#A17561FF",
              }}
            >
              <Settings className="w-4 h-4" />
              <span>Global Variables</span>
            </button>

            {/* Push New Defect Button - Solid Red */}
            <button
              onClick={() => setShowPushDefectModal(true)}
              disabled={!clientId || !projectId}
              className="flex items-center font-medium text-white hover:opacity-90 transition-opacity"
              style={{
                height: "38px",
                padding: "0 16px",
                borderRadius: "18px",
                gap: "8px",
                fontSize: "14px",
                fontWeight: 500,
                backgroundColor: "#DC2626",
              }}
            >
              <AlertCircle className="w-4 h-4" />
              <span>Push New Defect</span>
            </button>

            {/* Run Test Button - Solid Primary Blue */}
            <button
              onClick={() => {
                navigate(
                  `/test-runs?project_id=${projectId}&project_name=${encodeURIComponent(projectName)}&client_id=${clientId}&view_only=true`,
                );
              }}
              className="flex items-center font-medium text-white hover:opacity-90 transition-opacity"
              style={{
                height: "38px",
                padding: "0 16px",
                borderRadius: "18px",
                gap: "8px",
                fontSize: "14px",
                fontWeight: 500,
                backgroundColor: " #A17561FF",
              }}
            >
              <Play className="w-4 h-4" />
              <span>Run Test</span>
            </button>
          </div>
        </div>
        <div
          className={`flex-1 flex flex-nowrap min-h-0 min-w-0 ${isProjectEmpty ? "items-center justify-center" : ""}`}
          style={{
            minHeight: 0,
            height: "100%",
            gap: 0,
            overflow: "hidden",
          }}
        >
          {/* Only render sidebar if sections exist */}
          {sectionStructure && sectionStructure.length > 0 && (
            <div
              className="bg-[#E3E1DFFF] rounded-l-lg rounded-r-none shadow flex flex-col flex-shrink-0"
              style={{
                width: "240px",
                height: "100%",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Toolbar - Top of section panel */}
              <div
                className="flex items-center justify-between flex-shrink-0"
                style={{
                  height: "48px",
                  borderBottom: "1px solid #E5E7EB",
                  padding: "0 12px",
                }}
              >
                {/* Left-aligned dropdown: "Subgroups" / "All" */}
                <select
                  value={sidebarMode}
                  onChange={(e) => {
                    setSidebarMode(e.target.value);
                    // In 'all' mode, ensure null section so all sections show
                    if (e.target.value === "all") {
                      setCurrentSection(null);
                    }
                  }}
                  className="text-sm font-medium text-gray-700 bg-transparent border-none outline-none cursor-pointer"
                  style={{ fontSize: "14px", fontWeight: 500 }}
                >
                  <option value="subgroups">Subgroups</option>
                  <option value="all">All</option>
                </select>

                {/* Right-aligned button: + Add Section */}
                {effectiveCanCreateSection && (
                  <button
                    onClick={() => handleCreateSection(null)}
                    className="text-sm font-medium text-[#2E71BDFF] flex items-center gap-1 bg-transparent border-none cursor-pointer"
                    style={{ fontSize: "14px", fontWeight: 500 }}
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Section</span>
                  </button>
                )}
              </div>

              {/* Section Tree / All Sections List */}
              <div
                className="flex-1 overflow-y-auto overflow-x-hidden section-scroll-container"
                style={{
                  minHeight: 0,
                  maxHeight: "100%",
                  padding: "8px 0",
                }}
                onWheel={(e) => e.stopPropagation()}
              >
                {sidebarMode === "subgroups" ? (
                  <SectionTree
                    sections={sectionStructure}
                    currentSectionId={currentSection}
                    onSectionSelect={handleSectionSelect}
                    onCreateSection={
                      effectiveCanCreateSection ? handleCreateSection : null
                    }
                    onEditSection={
                      effectiveCanEditTestCase ? handleEditSection : null
                    }
                    onDeleteSection={
                      effectiveCanDeleteTestCase ? handleDeleteSection : null
                    }
                    onDropTestCase={
                      effectiveCanEditTestCase ? handleMoveTestCase : null
                    }
                    loading={sectionLoading}
                    projectStructure={projectStructure}
                  />
                ) : (
                  /* All mode: same SectionTree but clicking scrolls to section */
                  <SectionTree
                    sections={sectionStructure}
                    currentSectionId={scrollToSectionId}
                    onSectionSelect={(sectionId) => {
                      // setCurrentSection(null);
                      setScrollToSectionId(sectionId);
                    }}
                    onCreateSection={
                      effectiveCanCreateSection ? handleCreateSection : null
                    }
                    onEditSection={
                      effectiveCanEditTestCase ? handleEditSection : null
                    }
                    onDeleteSection={
                      effectiveCanDeleteTestCase ? handleDeleteSection : null
                    }
                    onDropTestCase={
                      effectiveCanEditTestCase ? handleMoveTestCase : null
                    }
                    loading={sectionLoading}
                    projectStructure={projectStructure}
                  />
                )}
              </div>
            </div>
          )}

          {/* Test Cases - Center Panel - Flexible */}
          {/* When project is empty, this becomes the empty state container */}
          <div
            className={`flex flex-col flex-1 bg-bg-primary shadow min-w-0 ${
              sectionStructure && sectionStructure.length > 0
                ? "rounded-r-lg rounded-l-none"
                : "rounded-lg"
            }`}
            style={{
              height: "100%",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            {/* Page Header - Always Visible */}

            {isProjectEmpty ? (
              // Empty State - Centered in available space
              <div className="flex-1 flex flex-wrap items-center justify-center w-full">
                <TestCasesEmptyState
                  onCreateSection={() => handleCreateSection(null)}
                  onCreateTestCase={handleCreateTestCase}
                  onGenerateAI={handleGenerateAI}
                  canCreateSection={effectiveCanCreateSection}
                  canCreateTestCase={effectiveCanCreateTestCase}
                  canGenerateAI={
                    canGenerateAI &&
                    !isViewOnlyMode &&
                    projectSettings.ai_test_case_generation
                  }
                  showAddSection={
                    !sectionStructure || sectionStructure.length === 0
                  }
                />
              </div>
            ) : (
              // Normal Test Case View
              <div
                className="flex-1 overflow-y-auto overflow-x-visible test-cases-scroll-container"
                style={{
                  minHeight: 0,
                  maxHeight: "100%",
                }}
                onWheel={(e) => e.stopPropagation()}
              >
                <div
                  className={`p-4 min-w-0 ${showHistoryPanel ? "!pr-0" : ""}`}
                  style={{ paddingRight: "0px !important" }}
                >
                  <SectionBasedTestCaseView
                    projectId={projectId}
                    selectedSectionId={currentSection}
                    sections={sectionStructure}
                    filters={filters}
                    branchId={branchId}
                    externalTestCases={
                      currentSection === null ? allTestCasesFlat : null
                    }
                    scrollToSectionId={
                      sidebarMode === "all" ? scrollToSectionId : null
                    }
                    onScrollHandled={() => setScrollToSectionId(null)}
                    activeTestCaseId={activeTestCaseId}
                    onViewTestCase={handleViewTestCase}
                    onEditTestCase={handleEditTestCase}
                    onDeleteTestCase={handleDeleteTestCase}
                    onAssignTestCase={handleAssignTestCase}
                    onEditSection={handleEditSection}
                    onAddCase={(section) => {
                      setCurrentSection(section.section_id);
                      handleCreateTestCase(section.section_id);
                    }}
                    onAddSubsection={(section) => {
                      handleCreateSection(section.section_id);
                    }}
                    onDeleteSection={handleDeleteSection}
                    canEdit={effectiveCanEditTestCase}
                    canDelete={effectiveCanDeleteTestCase}
                    canAssign={effectiveCanEditTestCase}
                    canAdd={effectiveCanCreateTestCase}
                    onOpenFilter={() => {
                      setTempFilters((prev) => ({ ...prev, ...filters }));
                      setShowFilterPanel(true);
                    }}
                    onOpenAI={
                      projectSettings.ai_test_case_generation
                        ? () => setShowAIModal(true)
                        : null
                    }
                    onOpenHistory={(tcId) => {
                      setLastTestCaseForHistory(tcId);
                      setHistoryTestCaseId(tcId);
                      setShowHistoryPanel(true);
                      setShowDetailPanel(false);
                      setSelectedTestCaseId(null);
                      setActiveTestCaseId(tcId);
                    }}
                    exportSelectionMode={exportSelectionMode}
                    onSelectedTestCasesChange={setSelectedTestCaseIdsForExport}
                  />{" "}
                </div>
              </div>
            )}
          </div>

          {/* Right Information Panel - Fixed 320px - Show ONLY when project is empty */}
          {/* Right Information Panel removed - moved to TestCasesEmptyState */}

          {/* History Panel - slides in from right inside the layout */}
          {showHistoryPanel && (
            <div
              className="flex-shrink-0 bg-white border-l border-gray-200 flex flex-col"
              style={{ width: "520px", height: "100%", overflow: "hidden" }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center gap-2">
                  {/* Left arrow: collapse panel */}
                  <button
                    onClick={() => {
                      setShowHistoryPanel(false);
                      setHistoryTestCaseId(null);
                      setActiveTestCaseId(null);
                    }}
                    className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                    title="Close panel"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <h2 className="text-base font-semibold text-gray-900">
                    Test Case History
                  </h2>
                </div>
                {/* Prev / Next navigation */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => navigateTestCase("prev")}
                    disabled={
                      allTestCasesFlat.findIndex(
                        (tc) => tc.test_case_id === historyTestCaseId,
                      ) <= 0
                    }
                    className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-30"
                    title="Previous test case"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 15l7-7 7 7"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => navigateTestCase("next")}
                    disabled={
                      allTestCasesFlat.findIndex(
                        (tc) => tc.test_case_id === historyTestCaseId,
                      ) >=
                      allTestCasesFlat.length - 1
                    }
                    className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-30"
                    title="Next test case"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                <TestCaseHistoryPage testCaseId={historyTestCaseId} />
              </div>
            </div>
          )}

          {/* Detail Panel - inline right panel */}
          {showDetailPanel && (
            <div
              className="flex-shrink-0"
              style={{ height: "100%", overflow: "hidden" }}
            >
              <TestCaseDetailPanel
                fromMain={true}
                testCaseId={selectedTestCaseId}
                projectId={projectId}
                isOpen={showDetailPanel}
                onClose={handleCloseDetailPanel}
                viewMode={detailPanelViewMode}
                onUpdate={handlePanelUpdate}
                isViewOnlyMode={isViewOnlyMode}
                onPrev={() => navigateTestCase("prev")}
                onNext={() => navigateTestCase("next")}
                hasPrev={
                  allTestCasesFlat.findIndex(
                    (tc) => tc.test_case_id === selectedTestCaseId,
                  ) > 0
                }
                hasNext={
                  allTestCasesFlat.findIndex(
                    (tc) => tc.test_case_id === selectedTestCaseId,
                  ) <
                  allTestCasesFlat.length - 1
                }
              />
            </div>
          )}
        </div>
      </div>

      {/* Test Case Form Modal */}
      <TestCaseFormModal
        isOpen={showTestCaseModal}
        onClose={() => {
          setShowTestCaseModal(false);
          setEditingTestCase(null);
          setIsViewMode(false);
          // Restore sidebar state when closing modal
          sessionStorage.removeItem("sidebarCollapsed");
          window.dispatchEvent(
            new CustomEvent("sidebarCollapse", {
              detail: { collapsed: false },
            }),
          );
        }}
        onSubmit={handleTestCaseSubmit}
        initialData={editingTestCase}
        projectId={projectId}
        defaultSectionId={currentSection}
        suiteId={suiteId}
        viewMode={isViewMode}
        onEdit={() => {
          // Switch from view mode to edit mode
          setIsViewMode(false);
        }}
      />

      {/* Section Form Modal */}
      <SectionFormModal
        isOpen={showSectionModal}
        projectStructure={projectStructure}
        onClose={() => {
          setShowSectionModal(false);
          setEditingSection(null);
          // Clear pending action if user cancels
          if (pendingAction) {
            setPendingAction(null);
          }
        }}
        onSubmit={handleSectionSubmit}
        initialData={editingSection}
        projectId={projectId}
        sections={sectionStructure}
        rootSectionId={null}
        rootSectionName=""
        isFirstTimeFlow={
          pendingAction === "create_test_case" && !checkSectionsExist()
        }
      />

      {/* Delete Section Modal */}
      <DeleteSectionModal
        isOpen={showDeleteSectionModal}
        onClose={() => {
          setShowDeleteSectionModal(false);
          setSectionToDelete(null);
        }}
        onConfirm={handleConfirmDeleteSection}
        section={sectionToDelete}
        sections={fullSectionStructure?.children || sectionStructure || []}
        testCaseCount={
          sectionToDelete
            ? getSectionTestCaseCount(sectionToDelete.section_id)
            : 0
        }
        subsectionCount={
          sectionToDelete ? getSubsectionCount(sectionToDelete.section_id) : 0
        }
      />

      {/* Test Steps Modal */}
      {showStepsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-bg-primary rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-text-primary">
                Test Steps
              </h2>
              <button
                onClick={() => setShowStepsModal(false)}
                className="text-text-tertiary hover:text-text-secondary"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {stepsLoading ? (
              <div className="py-4">
                <TableSkeleton rows={4} columns={2} />
              </div>
            ) : testSteps.length > 0 ? (
              <div className="space-y-4">
                {testSteps.map((step, index) => (
                  <div
                    key={step.step_id}
                    className="border border-border-primary rounded-lg p-4"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-800 text-sm font-medium rounded-full">
                          {step.step_number}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="mb-2">
                          <h4 className="text-sm font-medium text-text-primary mb-1">
                            Action:
                          </h4>
                          <p className="text-sm text-text-secondary">
                            {step.action}
                          </p>
                        </div>
                        {step.expected_result && (
                          <div className="mb-2">
                            <h4 className="text-sm font-medium text-text-primary mb-1">
                              Expected Result:
                            </h4>
                            <p className="text-sm text-text-secondary">
                              {step.expected_result}
                            </p>
                          </div>
                        )}
                        <div className="flex items-center space-x-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              step.status === "passed"
                                ? "bg-green-100 text-green-800"
                                : step.status === "failed"
                                  ? "bg-red-100 text-red-800"
                                  : step.status === "blocked"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : step.status === "skipped"
                                      ? "bg-bg-tertiary text-text-secondary"
                                      : "bg-info-light text-info"
                            }`}
                          >
                            {step.status || "pending"}
                          </span>
                          {step.actual_result && (
                            <span className="text-xs text-text-tertiary">
                              Actual: {step.actual_result}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-text-secondary">
                  No test steps found for this test case.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filter Side Panel */}
      {showFilterPanel && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-gray-500/20 backdrop-blur-sm z-40"
            onClick={() => setShowFilterPanel(false)}
          />
          {/* Side Panel */}
          <div className="fixed right-0 top-0 h-full w-96 bg-bg-primary shadow-xl z-50 overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-text-primary">
                  Filters
                </h2>
                <button
                  onClick={() => setShowFilterPanel(false)}
                  className="text-text-tertiary hover:text-text-secondary"
                >
                  <Close className="w-6 h-6" />
                </button>
              </div>

              {/* Status Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Status
                </label>
                <select
                  value={tempFilters.status}
                  onChange={(e) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 bg-bg-primary text-text-primary border border-border-primary rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="review">Review</option>
                  <option value="requested_changes">Requested Changes</option>
                  <option value="rejected">Rejected</option>
                  <option value="deprecated">Deprecated</option>
                </select>
              </div>

              {/* Tags Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Tags
                </label>
                <select
                  value={tempFilters.tags.length > 0 ? tempFilters.tags[0] : ""}
                  onChange={(e) => {
                    const selectedTag = e.target.value;
                    setTempFilters((prev) => ({
                      ...prev,
                      tags: selectedTag ? [selectedTag] : [],
                    }));
                  }}
                  className="w-full px-3 py-2 bg-bg-primary text-text-primary border border-border-primary rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Tags</option>
                  {availableTags.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
                {availableTags.length === 0 && !usersLoading && (
                  <p className="mt-2 text-xs text-text-tertiary">
                    No tags available in this project
                  </p>
                )}
              </div>

              {/* Created By Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Created By
                </label>
                <select
                  value={tempFilters.created_by}
                  onChange={(e) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      created_by: e.target.value,
                    }))
                  }
                  disabled={usersLoading}
                  className="w-full px-3 py-2 bg-bg-primary text-text-primary border border-border-primary rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-bg-tertiary"
                >
                  <option value="">All Users</option>
                  {projectUsers.map((user) => (
                    <option key={user.user_id} value={user.user_id}>
                      {user.full_name || "Unknown User"}
                    </option>
                  ))}
                </select>
                {projectUsers.length === 0 && !usersLoading && (
                  <p className="mt-2 text-xs text-text-tertiary">
                    No users found in this project
                  </p>
                )}
              </div>

              {/* Assign To Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Assign To
                </label>
                <select
                  value={tempFilters.assignedTo ?? "all"}
                  onChange={(e) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      assignedTo: e.target.value,
                    }))
                  }
                  disabled={usersLoading}
                  className="w-full px-3 py-2 bg-bg-primary text-text-primary border border-border-primary rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-bg-tertiary"
                >
                  <option value="all">All</option>
                  <option value="unassigned">Unassigned</option>
                  {projectUsers.map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.full_name || "Unknown User"}
                    </option>
                  ))}
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-border-primary">
                <button
                  onClick={() => {
                    setTempFilters({
                      status: "",
                      tags: [],
                      created_by: "",
                      assignedTo: "",
                    });
                    setFilters({
                      status: "",
                      tags: [],
                      created_by: "",
                      assignedTo: "",
                    });
                    setShowFilterPanel(false);
                    setRefreshTrigger((prev) => prev + 1);
                  }}
                  className="flex-1 px-4 py-2 border border-border-primary rounded-md text-sm font-medium text-text-primary hover:bg-bg-hover"
                >
                  Clear All
                </button>
                <button
                  onClick={() => {
                    setFilters({ ...tempFilters });
                    setShowFilterPanel(false);
                    setRefreshTrigger((prev) => prev + 1);
                  }}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* AI Generate Test Cases Modal */}
      <AIGenerateTestCasesModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onTestCasesGenerated={handleAITestCasesGenerated}
        projectId={projectId}
        defaultSectionId={currentSection}
      />

      {/* Test Cases Preview Modal */}
      <TestCasesPreviewModal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewTestCases(null);
          setPreviewMetadata(null);
        }}
        testCases={previewTestCases}
        onConfirm={handlePreviewConfirm}
        projectId={projectId}
        sectionId={previewMetadata?.sectionId || currentSection}
        reviewerId={previewMetadata?.reviewerId || null}
      />

      <PushNewDefectModal
        open={showPushDefectModal}
        onClose={() => setShowPushDefectModal(false)}
        clientId={clientId}
        projectId={projectId}
        onSuccess={() => setRefreshTrigger((t) => t + 1)}
      />
    </ErrorBoundary>
  );
};

export default TestCaseManagementPage;
